import React, { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import Modal from './Modal';

export default function ConfirmDialog({
  open,
  title = 'Are you sure?',
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  onConfirm,
  onClose,
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const handleConfirm = async () => {
    setBusy(true);
    setError('');
    try {
      await onConfirm();
      setBusy(false);
      onClose?.();
    } catch (err) {
      setBusy(false);
      setError(err?.message || 'Something went wrong.');
    }
  };

  const confirmBase =
    'inline-flex items-center justify-center px-5 py-2.5 rounded-full text-sm font-semibold transition-all disabled:opacity-60 disabled:cursor-not-allowed';
  const confirmClass = destructive
    ? `${confirmBase} bg-red-500/90 text-white hover:bg-red-500 shadow-lg shadow-red-500/20`
    : `${confirmBase} bg-[#B0FC38] text-gray-900 hover:bg-[#9BE22E] shadow-lg shadow-[#B0FC38]/20`;

  return (
    <Modal
      open={open}
      onClose={busy ? () => {} : onClose}
      title={title}
      maxWidth="max-w-md"
      footer={
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="px-4 py-2.5 rounded-full border border-white/[0.1] text-sm text-gray-300 hover:text-white hover:border-white/[0.2] transition-all disabled:opacity-60"
          >
            {cancelLabel}
          </button>
          <button type="button" onClick={handleConfirm} disabled={busy} className={confirmClass}>
            {busy ? (
              <span className="inline-flex items-center gap-2">
                <span className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                Working…
              </span>
            ) : (
              confirmLabel
            )}
          </button>
        </div>
      }
    >
      <div className="flex gap-4">
        {destructive && (
          <div className="w-9 h-9 rounded-xl bg-red-500/[0.1] border border-red-500/20 flex items-center justify-center shrink-0">
            <AlertTriangle className="w-4 h-4 text-red-400" />
          </div>
        )}
        <div className="flex-1">
          {description && <p className="text-sm text-gray-400 leading-relaxed whitespace-pre-line">{description}</p>}
          {error && (
            <div className="mt-3 px-3 py-2 rounded-lg bg-red-500/[0.08] border border-red-500/20 text-[13px] text-red-300/90">
              {error}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
